<template>
    <Card v-if="!hasActivatedDraftNumbering" class="draft-numbering">
        <div class="draft-numbering__information">
            <Heading
                :labelTitle="
                    $t.__('settings.sellsymodule.invoicing.conformity.draft_numbering.title')
                "
                variant="h2"
                :prefixColoredIcon="{ icon: 'warning', color: 'brand' }"
            />
            <p
                class="typographies-paragraph-medium-regular"
                v-html="
                    $t.__(
                        'settings.sellsymodule.invoicing.conformity.draft_numbering.introduction',
                        {
                            boldStart: boldWithFontClass,
                            boldEnd: '</strong>',
                        },
                    )
                "
            />
            <CompassLink
                :label="$t.__('settings.sellsymodule.invoicing.conformity.draft_numbering.link_1')"
                link="https://help.sellsy.com/fr/articles/13728288-activation-de-la-numerotation-brouillon"
                target="_blank"
            />
            <h3 class="draft-numbering__subtitle typographies-heading-h3">
                {{ $t.__('settings.sellsymodule.invoicing.conformity.draft_numbering.subtitle') }}
            </h3>
            <ul class="draft-numbering__steps">
                <li
                    v-for="value in draftNumberingSteps"
                    :key="value"
                    class="typographies-paragraph-medium-regular"
                >
                    {{ $t.__(value) }}
                </li>
            </ul>
            <CompassLink
                :label="$t.__('common.labels.learn_more')"
                link="https://help.sellsy.com/fr/articles/13728288-activation-de-la-numerotation-brouillon"
                target="_blank"
            />
        </div>
        <div class="draft-numbering__count">
            <ConformitySettingsDraftNumbering
                :invoiceCount="invoiceCount"
                :creditNoteCount="creditNoteCount"
            />
            <CompassTooltip
                :content="$t.__('common.labels.no_permissions_to_perform_this_action')"
                :disabled="isCreator"
                :arrow="false"
                class="draft-numbering__activate-tooltip"
            >
                <CompassButton
                    class="draft-numbering__activate-button"
                    leftIcon="shield-check"
                    :label="
                        $t.__('settings.sellsymodule.invoicing.conformity.draft_numbering.activate')
                    "
                    :disabled="!isReadyToActivateDraftNumbering"
                    data-bot="compliance_enable-draft-numbering"
                    @click="openActivationConfirmationModal"
                />
            </CompassTooltip>
        </div>
    </Card>
    <Card
        class="conformity-settings"
        :class="{ 'conformity-settings--is-disabled': !hasActivatedDraftNumbering }"
    >
        <Heading
            :labelTitle="$t.__('settings.sellsymodule.invoicing.conformity.title')"
            variant="h2"
            :prefixColoredIcon="{ icon: 'file-text-shield', color: 'brand' }"
        />
        <CompassAlert
            v-if="!isConform"
            :description="$t.__('settings.sellsymodule.invoicing.conformity.not_conform_alert')"
            type="warning"
            :showClose="false"
        />
        <div class="conformity-settings__help typographies-paragraph-medium-regular">
            {{ $t.__('settings.sellsymodule.invoicing.conformity.information_text') }}
            <CompassLink
                :label="$t.__('common.labels.learn_more')"
                link="https://help.sellsy.com/fr/articles/6100605-activer-le-mode-conforme-pour-les-documents-de-vente"
                target="_blank"
            />
        </div>

        <CompassSkeleton v-if="isLoading" variant="block" />

        <template v-else>
            <div v-if="isConform" class="conformity-settings__is-conform">
                <div class="typographies-heading-h4 conformity-settings__is-conform--title">
                    {{ $t.__('settings.sellsymodule.invoicing.conformity.is_conform_title') }}
                </div>
                <div class="conformity-settings__is-conform--rules-container">
                    <div
                        v-for="(ruleText, index) in isConformRuleText"
                        :key="index"
                        class="typographies-paragraph-medium-regular conformity-settings__is-conform--rule"
                    >
                        <CompassIcon icon="success-circle-fill" type="success" />
                        {{ ruleText }}
                    </div>
                </div>
                <div
                    v-if="shouldDisplayActivatedInfo"
                    class="conformity-settings__activated-data typographies-paragraph-medium-regular"
                >
                    <div v-if="!!conformityData.activated_at">
                        {{
                            $filters.dateFormatter(
                                conformityData.activated_at,
                                'NORMAL_DATE_TIME_WITH_DAY',
                            )
                        }}
                    </div>
                    <div>
                        {{ textActivatedBy }}
                    </div>
                </div>
                <CompassTooltip
                    :content="contentTooltipDownloadDisabled"
                    :disabled="downloadCertificateEnabled"
                    placement="bottom-start"
                >
                    <CompassButton
                        class="conformity-settings__is-conform--download-certificate"
                        :disabled="!downloadCertificateEnabled"
                        :label="
                            $t.__('settings.sellsymodule.invoicing.conformity.download_certificate')
                        "
                        @click="downloadCertificate"
                    />
                </CompassTooltip>
            </div>
            <div v-else class="conformity-settings__is-not-conform">
                <CompassObject
                    :label="$t.__('settings.sellsymodule.invoicing.conformity.not_conform_title')"
                    object="document"
                    size="small"
                />
                <CompassTooltip
                    :content="$t.__('common.labels.no_permissions_to_perform_this_action')"
                    :disabled="canActivateConformity"
                    :arrow="false"
                >
                    <CompassButton
                        :disabled="!canActivateConformity"
                        :label="
                            $t.__(
                                'settings.sellsymodule.invoicing.conformity.button_activate_conformity',
                            )
                        "
                        data-bot="compliance_enable-compliant-mode"
                        @click="handleActivateConformity"
                    />
                </CompassTooltip>
            </div>
        </template>
    </Card>
</template>
<script setup>
import { ref, computed, onMounted, onUnmounted, inject } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import Card from 'vcomponents/common/card/Card.vue';
import Heading from 'vcomponents/common/Heading/Heading.vue';
import { getConformityData } from 'api/settings/invoicing/conformity/ConformityService';
import { getFullNameUser } from 'Helpers/avatarHelper';
import ConformitySettingsDraftNumbering from 'vcomponents/settings/sellsymodule/invoicing/ConformitySettingsDraftNumbering.vue';

const props = defineProps({
    isCreatable: {
        type: Boolean,
        required: true,
    },
    hasDraftNumbering: {
        type: Boolean,
        default: false,
    },
    invoiceCount: {
        type: Number,
        default: 0,
    },
    creditNoteCount: {
        type: Number,
        default: 0,
    },
});

const store = useStore();
const route = useRoute();

// Injected
const $t = inject('$t');
const $pushToast = inject('$pushToast');
const $ajax = inject('$ajax');

// Ref
const hasActivatedDraftNumbering = ref(props.hasDraftNumbering);
const conformityData = ref(null);
const isLoading = ref(false);

const isConformRuleText = [
    $t.__('settings.sellsymodule.invoicing.conformity.is_conform_credit_note_creation'),
    $t.__('settings.sellsymodule.invoicing.conformity.is_conform_invoice_number'),
    $t.__('settings.sellsymodule.invoicing.conformity.is_conform_invoice_date'),
];

const draftNumberingSteps = [
    'settings.sellsymodule.invoicing.conformity.draft_numbering.steps.1',
    'settings.sellsymodule.invoicing.conformity.draft_numbering.steps.2',
    'settings.sellsymodule.invoicing.conformity.draft_numbering.steps.3',
    'settings.sellsymodule.invoicing.conformity.draft_numbering.steps.4',
];

const boldWithFontClass = `<strong class="typographies-paragraph-medium-semibold">`;

// Computed
const isTrial = computed(() => store.getters['corp/getIsTrial']);
const isCreator = computed(() => store.state.user.isCreator);
const errorsHandler = computed(() => store.state.system.errorsHandler);

const isReadyToActivateDraftNumbering = computed(() => {
    return props.invoiceCount === 0 && props.creditNoteCount === 0 && isCreator.value;
});

const shouldDisplayActivatedInfo = computed(() => {
    return !!conformityData.value?.activated_at && !!conformityData.value?.activated_by;
});

const downloadCertificateEnabled = computed(() => {
    return !isTrial.value && !!conformityData.value?.pdf;
});

const textActivatedBy = computed(() => {
    if (!conformityData.value?.activated_by) {
        return '';
    }
    return $t.__('settings.sellsymodule.invoicing.conformity.activated_by', {
        fullName: getFullNameUser(conformityData.value.activated_by),
        email: conformityData.value.activated_by.email,
    });
});

const contentTooltipDownloadDisabled = computed(() => {
    if (isTrial.value) {
        return $t.__('settings.sellsymodule.invoicing.conformity.download_is_trial');
    }
    if (!conformityData.value?.pdf) {
        return $t.__('settings.sellsymodule.invoicing.conformity.certificate_is_generating');
    }
    return '';
});

const isConform = computed(() => {
    return !!conformityData.value && conformityData.value.active;
});

const canActivateConformity = computed(() => {
    return hasActivatedDraftNumbering.value && props.isCreatable;
});

const downloadCertificate = () => {
    window.open(conformityData.value?.pdf, '_blank');
};

const handleActivateConformity = () => {
    window.location.href = '/documents/preferences/conformity/enable';
};

const handleConformityGenerate = (event) => {
    if (conformityData.value) {
        conformityData.value.pdf = event.pdf.url;
    }
};

const openActivationConfirmationModal = () => {
    store.dispatch('popupPanel/open', {
        key: 'draftNumberingConfirmationModal',
        props: {},
    });
};

const handleDraftNumberingActivation = () => {
    $ajax.post(
        '/rest/documents/conformity-preferences/activate-draft-numbering',
        {},
        {},
        () => {
            hasActivatedDraftNumbering.value = true;
            $pushToast({
                type: 'success',
                icon: true,
                text: $t.__(
                    'settings.sellsymodule.invoicing.conformity.draft_numbering.activate.success',
                ),
            });
        },
        (error) => {
            $pushToast({
                type: 'error',
                icon: true,
                text: $t.__('common.labels.error_retry'),
            });
            errorsHandler.value.logException(error);
        },
    );
};

onMounted(async () => {
    isLoading.value = true;
    try {
        conformityData.value = (await getConformityData()).data;
        EventBus.on('conformity:generate', handleConformityGenerate);
        EventBus.on('conformity:activateDraftNumbering', handleDraftNumberingActivation);
    } catch (error) {
        $pushToast({
            type: 'error',
            icon: true,
            text: $t.__('settings.sellsymodule.invoicing.conformity.error_get_data'),
        });
        console.error(error);
        errorsHandler.value.logException(error);
    } finally {
        isLoading.value = false;
    }

    if (Object.hasOwn(route.query, 'success')) {
        $pushToast({
            type: 'success',
            icon: true,
            text: $t.__(
                'settings.sellsymodule.invoicing.enable_conformity.success_conformity_activation',
            ),
        });
        const url = new URL(location.href);
        if (url.search.length > 0) {
            url.search = '';
            history.replaceState(null, document.title, url.toString());
        }
    }
});

onUnmounted(() => {
    EventBus.off('conformity:generate', handleConformityGenerate);
    EventBus.off('conformity:activateDraftNumbering', handleDraftNumberingActivation);
});
</script>

<style scoped lang="scss">
.conformity-settings {
    &--is-disabled {
        opacity: 0.5;
        pointer-events: none;
    }

    .typographies-heading-h2 {
        margin-bottom: var(--spacing-3);
    }

    &__help {
        margin-bottom: var(--spacing-3);
    }

    &__is-conform {
        border: 1px solid var(--colors-common-border-default);
        border-radius: var(--spacing-0);
        padding: var(--spacing-3);

        &--title {
            margin-bottom: var(--spacing-1);
        }

        &--rule {
            display: flex;
            gap: var(--spacing-0);
        }

        &--rules-container {
            display: flex;
            flex-direction: column;
            gap: var(--spacing-1);
            margin-bottom: var(--spacing-4);

            &:has(+ .conformity-settings__activated-data) {
                border-bottom: 1px solid var(--colors-common-border-default);
                padding-bottom: var(--spacing-3);
            }
        }

        .conformity-settings__activated-data {
            color: var(--colors-common-text-standard-caption);
            margin-bottom: var(--spacing-3);
        }
    }

    &__is-not-conform {
        background-color: var(--colors-common-background-subtle);
        border-radius: var(--spacing-0);
        display: flex;
        gap: var(--spacing-1);
        justify-content: space-between;
        padding: var(--spacing-3);
    }
}

.draft-numbering {
    display: grid;
    grid-template-columns: repeat(12, 1fr);
    gap: var(--spacing-3);

    &__information {
        grid-column: span 8;

        @media (max-width: 1199px) {
            grid-column: span 12;
        }

        p {
            margin: 0 0 var(--spacing-1);
        }
    }

    &__subtitle {
        margin: var(--spacing-3) 0 var(--spacing-1);
    }

    &__steps {
        list-style: disc;
        margin: 0 0 var(--spacing-1);
        padding: 0 0 0 20px;
    }

    &__count {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-3);
        grid-column: 10 / 3 span;

        @media (max-width: 1199px) {
            grid-column: 4 / 6 span;
        }
    }

    &__activate-tooltip,
    &__activate-button {
        width: 100%;
    }

    &__activate-tooltip {
        :deep(.compass-tooltip__element) {
            width: 100%;
        }
    }
}
</style>
